import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Typography } from '@telavita-core/react-design-kit'
import { endOfMonth, format, startOfMonth, subMonths } from 'date-fns'
import { ptBR } from 'date-fns/locale'

import { NpsHelpModal } from 'containers/ModalManager/Modals/NpsHelpModal'
import { NpsHowToImproveModal } from 'containers/ModalManager/Modals/NpsHowToImproveModal'

import { GraphicsCard } from 'components/GraphicsCard'
import HelpFilledIcon from 'components/CoreIcons/Icons/Help'
import ArrowLeftIcon from 'components/CoreIcons/Icons/ArrowLeft'
import ArrowRightIcon from 'components/CoreIcons/Icons/ArrowRight'
import { NPSGraphic } from './components/NPSGraphic'

import { useGetProfessionalNPS, IMonthlyNpsAverage } from './hooks/useGetProfessionalNPS'

import { 
  Arrows, 
  Container, 
  IconButton, 
  Footer
} from './styles'

const DATA_LENGTH = 6
const PREVIOUS_MONTHS = 5

const lastMonth = subMonths(new Date(), 1)
const previousMonths = subMonths(lastMonth, PREVIOUS_MONTHS)

export const ProfessionalNPS = (): JSX.Element => {
  const [showHelpModal, setShowHelpModal] = useState(false)
  const [showHowToImproveModal, setShowHowToImproveModal] = useState(false)
  const [rightArrowDisable, setRightArrowDisable] = useState(false)
  const [npsData, setNpsData] = useState<IMonthlyNpsAverage[]>([])
  const [selectedDate, setSelectedDate] = useState({
    startDate: startOfMonth(previousMonths),
    endDate: startOfMonth(lastMonth),
  })

  const cardRef = useRef<HTMLDivElement>(null)

  const { getProfessionalNPS, loading, professionalNPS } = useGetProfessionalNPS()

  useEffect(() => {
    void getProfessionalNPS({
      startDate: format(startOfMonth(selectedDate.startDate), 'yyyy-MM-dd'),
      endDate: format(endOfMonth(selectedDate.endDate), 'yyyy-MM-dd'),
    })
  }, [getProfessionalNPS, selectedDate.startDate, selectedDate.endDate])

  useEffect(() => {
    if (professionalNPS?.monthlyAverage?.length) {
      const monthlyAverageData = professionalNPS?.monthlyAverage?.slice(-DATA_LENGTH)
      setNpsData(monthlyAverageData)
    }

  }, [professionalNPS])

  const isRightArrowDisable = useCallback(() => {
    const monthlyAverageDataFromHook = professionalNPS?.monthlyAverage[0]
    const dataValue = npsData[0]
    const isSameDate = 
      monthlyAverageDataFromHook?.month === dataValue?.month && 
      monthlyAverageDataFromHook?.year === dataValue?.year

    if (isSameDate) {
      return true
    }

    return false
  }, [professionalNPS, npsData])

  const getPreviousMonths = () => {
    const newEndDate = subMonths(selectedDate.startDate, 1)
    const newStartDate = subMonths(newEndDate, PREVIOUS_MONTHS)

    setSelectedDate({ startDate: newStartDate, endDate: newEndDate })
  }

  function navLeft() {
    const firstGraphDate = npsData[0]?.date

    const newEndDate = subMonths(firstGraphDate, 1)
    const newStartDate = subMonths(newEndDate, PREVIOUS_MONTHS)
    const formattedYear = format(newStartDate, 'yy')
    const formattedMonth = format(newStartDate, 'MMM', {
      locale: ptBR
    })

    const findFirstValueData = professionalNPS?.monthlyAverage?.findIndex(item => {
      return item?.month === formattedMonth && item?.year === formattedYear
    })
    
    if (findFirstValueData >= 0) {
      const newData = professionalNPS?.monthlyAverage?.slice(findFirstValueData, findFirstValueData + DATA_LENGTH)
      return setNpsData(newData)
    }

    getPreviousMonths()
  }

  function navRight() {
    const firstValue = npsData[0]
    const findFirstValueData = professionalNPS.monthlyAverage.findIndex(item => (item?.month === firstValue?.month && item?.year === firstValue?.year))

    if (professionalNPS.monthlyAverage.length > DATA_LENGTH) {
      setNpsData(professionalNPS.monthlyAverage.slice(findFirstValueData - DATA_LENGTH, findFirstValueData))
    }
  }

  useEffect(() => {
    setRightArrowDisable(() => isRightArrowDisable())
  }, [npsData, isRightArrowDisable])

  const biannualNps = professionalNPS?.monthlyAverage?.length 
    ? professionalNPS?.monthlyAverage[0]?.biannualAverage
    : 0

  return (
    <>
      <GraphicsCard
        id='sectionNPSScore'
        title='Nota NPS'
        headerAction={
          <IconButton onClick={() => setShowHelpModal(true)}>
            <HelpFilledIcon height={24} width={24} fill='#1F35B5'/>
          </IconButton>
        }
      >
        <Container ref={cardRef}>
          <Typography italic variant='content2'>
            Após a consulta, perguntamos ao paciente:<br />“O quanto você recomenda este profissional?”
          </Typography>

          <Arrows
            isLoading={loading}
            isEmpty={false}
          >
            <button onClick={navLeft}>
              <ArrowLeftIcon 
                fill='#4b4b4b'
                height='24px'
                width='24px'
              />
            </button>
            <button
              onClick={navRight}
              disabled={rightArrowDisable}
            >
              <ArrowRightIcon 
                fill='#4b4b4b'
                height='24px'
                width='24px'
              />
            </button>
          </Arrows>

          <NPSGraphic 
            npsData={npsData}
            biannualNps={biannualNps}
            loading={loading}
          />
          
          <Footer>
            <Button
              id='npsButton'
              uppercase={false}
              variant='text'
              onClick={() => setShowHowToImproveModal(true)}
            >
              Saiba como melhorar a sua nota
            </Button>
          </Footer>
        </Container>
      </GraphicsCard>

      {showHelpModal && ( 
        <NpsHelpModal onClose={() => setShowHelpModal(false)}/> 
      )}

      {showHowToImproveModal && (
        <NpsHowToImproveModal onClose={() => setShowHowToImproveModal(false)}/>
      )}
    </>
  )
}